/*
  All application override components must be imported here
  and added to the $data.componentOverrides object, which
  indicates if a standard component has a corresponding
  application specific override.

  Lifecycle method created() contains the resolution logic.

  Usage:
            <ComponentResolver
              componentId="TrackerDataTable"
              :componentProps="componentProps"
              :overrideComponentId="trackerType.dataTableComponentId"
              :selectedContext="selectedContext"
            />

  componentId:          component to be resolved and rendered
  componentProps:       an object containing all the props to be forward by the component to render (i.e. selectedContext, propTitle)
  overrideComponentId:  optional, if this is specified it will be rendered
*/

// common components
import AppWorkspace from '@/agilis/components/AppWorkspace/AppWorkspace.vue'
import DataTableV2ItemView from '@/agilis/components/DataTableV2/DataTableV2ItemView/DataTableV2ItemView.vue'
import DataTableV2ItemEdit from '@/agilis/components/DataTableV2/DataTableV2ItemEdit/DataTableV2ItemEdit.vue'
import DataTableV2ItemNestedTable from '@/agilis/components/DataTableV2/DataTableV2ItemNestedTable/DataTableV2ItemNestedTable.vue'
import DataTableV2Paginated from '@/agilis/components/DataTableV2Paginated/DataTableV2Paginated.vue'
import Dashboard from '@/views/Dashboard/Dashboard.vue'
import DashboardTabs from '@/agilis/components/DashboardTabs/DashboardTabs.vue'
import ReportTabs from '@/agilis/components/ReportTabs/ReportTabs.vue'
import DocumentTabs from '@/agilis/components/DocumentTabs/DocumentTabs.vue'
import TreeEditor from '@/agilis/components/TreeEditor/TreeEditor.vue'
import TreeEditorV2 from '@/agilis/components/TreeEditorV2/TreeEditorV2.vue'

// people components
import AppTabspeople from '@/applications/people/components/AppTabs/AppTabs.vue'

// crm components
import CustomerTableCrm from '@/applications/crm/components/CustomerTable/CustomerTable.vue'
import OpportunityTableEdit from '@/applications/crm/components/OpportunityTable/OpportunityTableEdit.vue'
import OpportunityTableView from '@/applications/crm/components/OpportunityTable/OpportunityTableView.vue'

// sales components
import Company2TableItemEdit from '@/applications/sales/components/Company2TableItemEdit/Company2TableItemEdit.vue'
import Company2TableItemView from '@/applications/sales/components/Company2TableItemView/Company2TableItemView.vue'
import Contact2Table from '@/applications/sales/components/Contact2Table/Contact2Table.vue'
import ContactTable from '@/applications/sales/components/ContactTable/ContactTable.vue'
import ContactTableItemEdit from '@/applications/sales/components/ContactTableItemEdit/ContactTableItemEdit.vue'
import CustomerTable from '@/applications/sales/components/CustomerTable/CustomerTable.vue'
import CustomerTableItemView from '@/applications/sales/components/CustomerTableItemView/CustomerTableItemView.vue'
import CustomerTableItemEdit from '@/applications/sales/components/CustomerTableItemEdit/CustomerTableItemEdit.vue'
import DeliveryTableItemView from '@/applications/sales/components/Delivery/DeliveryTableItemView/DeliveryTableItemView.vue'
import OrderTableItemView from '@/applications/sales/components/Order/OrderTableItemView/OrderTableItemView.vue'
import ProductTable from '@/applications/sales/components/ProductTable/ProductTable.vue'
import Product2Table from '@/applications/sales/components/Product2Table/ProductTable.vue'
import PropositionTableView from '@/applications/sales/components/PropositionTable/PropositionTableView.vue'
import PropositionTableEdit from '@/applications/sales/components/PropositionTable/PropositionTableEdit.vue'
import PropositionTable from '@/applications/sales/components/PropositionTable/PropositionTable.vue'
import PriceItemTable from '@/applications/sales/components/PriceItemTable/PriceItemTable.vue'
import PriceItemTableEdit from '@/applications/sales/components/PriceItemTable/datatable/PriceItemTableEdit.vue'
import PriceItemTableView from '@/applications/sales/components/PriceItemTable/datatable/PriceItemTableView.vue'

// scrum components
import TrackerDataTable from '@/applications/scrum/components/TrackerDataTable/TrackerDataTable.vue'
import TrackerDataTablePaginated from '@/applications/scrum/components/TrackerDataTable/TrackerDataTablePaginated.vue'
import TrackerDataTableItemAssociations from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableItemAssociations/TrackerDataTableItemAssociations.vue'
import TrackerDataTableItemChildren from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableItemChildren/TrackerDataTableItemChildren.vue'
import TrackerDataTableItemEdit from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableItemEdit/TrackerDataTableItemEdit.vue'
import TrackerDataTableView from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableView/TrackerDataTableView.vue'
import TrackerDataTableExpandRow from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableExpandRow/TrackerDataTableExpandRow.vue'
import TrackerKanban from '@/applications/scrum/components/TrackerKanban/TrackerKanban.vue'
import TrackerDashboard from '@/applications/scrum/components/TrackerDashboard/TrackerDashboard.vue'
import TrackerTabs from '@/applications/scrum/components/TrackerTabs/TrackerTabs.vue'

import { mapGetters } from 'vuex'

export default {
  name: 'ComponentResolver',

  props: {
    componentId: String,
    forwardRef: Function,
    componentProps: {
      type: Object,
      default: () => ({})
    }
  },

  components: {
    AppTabspeople,
    AppWorkspace,
    Company2TableItemEdit,
    Company2TableItemView,
    Contact2Table,
    ContactTable,
    ContactTableItemEdit,
    CustomerTable,
    CustomerTableItemView,
    CustomerTableItemEdit,
    CustomerTableCrm,
    DataTableV2ItemView,
    DataTableV2ItemEdit,
    DataTableV2ItemNestedTable,
    DataTableV2Paginated,
    Dashboard,
    DashboardTabs,
    DeliveryTableItemView,
    DocumentTabs,
    OpportunityTableEdit,
    OpportunityTableView,
    OrderTableItemView,
    ProductTable,
    Product2Table,
    PropositionTable,
    PropositionTableView,
    PropositionTableEdit,
    PriceItemTable,
    PriceItemTableEdit,
    PriceItemTableView,
    ReportTabs,
    TrackerDataTable,
    TrackerDataTablePaginated,
    TrackerDataTableItemAssociations,
    TrackerDataTableItemChildren,
    TrackerDataTableItemEdit,
    TrackerDataTableView,
    TrackerDataTableExpandRow,
    TrackerKanban,
    TrackerDashboard,
    TrackerTabs,
    TreeEditor,
    TreeEditorV2
  },

  data: () => ({
    overrideComponentId: '',
    resolvedComponentId: '',
    componentOverrides: {
      people: {
        AppWorkspace: 'AppWorkspace',
        TrackerDataTable: 'TrackerDataTablePaginated'
      },
      crm: {
        AppWorkspace: 'AppWorkspace',
        TrackerDataTable: 'TrackerDataTablePaginated'
      },
      plan: {
        AppWorkspace: 'AppWorkspace'
      },
      sales: {
        AppWorkspace: 'AppWorkspace',
        TrackerDataTable: 'TrackerDataTablePaginated'
      },
      scrum: {
        AppWorkspace: 'AppWorkspace',
        TrackerDataTable: 'TrackerDataTablePaginated'
      }
    }
  }),

  created: function () {
    // resolve to standard component
    this.resolvedComponentId = this.componentId
    // resolve to specified overrideComponentId if any
    if (this.$attrs.overrideComponentId) {
      this.resolvedComponentId = this.$attrs.overrideComponentId
    } else if (this.applicationId) {
      // resolve to application override if one exists
      const appOverrides = this.$data.componentOverrides[this.applicationId]
      if (appOverrides && appOverrides[this.componentId]) {
        this.resolvedComponentId = appOverrides[this.componentId]
      }
    }
  },

  mounted: function () {
    if (typeof this.forwardRef === 'function') {
      this.forwardRef(this.$refs.componentResolved)
    }
  },

  computed: {
    ...mapGetters({
      'applicationId': 'contextStore/applicationId'
    }),

    forwardProps () {
      return {
        ...this.componentProps
      } // add here props that need to be forwarded to the final component
    }
  }
}
